import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import api from "../../../axiosConfig";
import { popup } from "../../common/popup";
import "./css/ShortLifetimePage.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const emptyItem = {
  id: null,
  spesifikasi: "",
  part_name: "",
  lokasi: "",
  maker: "",
  stock: "",
  minimum_stock: "",
  keterangan: "",
  stock_old: 0, // simpan stock lama saat edit
};

export default function StockShortTab() {
  const [kategori, setKategori] = useState("bearing");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyItem);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [search, setSearch] = useState("");

  // Kategori (dikelola admin, tersimpan di backend)
  const [categories, setCategories] = useState([]);
  const [showCatModal, setShowCatModal] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [savingCat, setSavingCat] = useState(false);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    fetchItems();
    setSearch("");
  }, [kategori]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get("/stock-items", {
        params: { lifetime_type: "short", category: kategori },
      });
      setItems(res.data || []);
    } catch (err) {
      console.error("FETCH ERROR:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/stock-items/categories");
      const list = res.data.data || [];
      setCategories(list);
      // pastikan kategori terpilih valid
      if (list.length && !list.includes(kategori)) setKategori(list[0]);
    } catch (err) {
      console.error("Gagal ambil kategori:", err);
    }
  };

  const handleAddCategory = async () => {
    const name = newCat.trim().toLowerCase();
    if (!name) {
      popup.alert("Nama kategori wajib diisi");
      return;
    }
    setSavingCat(true);
    try {
      const res = await api.post("/stock-items/categories", { name });
      if (res.data.success) {
        setNewCat("");
        setShowCatModal(false);
        await fetchCategories();
        setKategori(name);
      } else {
        popup.alert(res.data.message || "Gagal menambah kategori");
      }
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal menambah kategori");
    } finally {
      setSavingCat(false);
    }
  };

  const handleDeleteCategory = async (name) => {
    const ok = await popup.confirm(`Hapus kategori "${name.toUpperCase()}"?`);
    if (!ok) return;
    try {
      const res = await api.delete(
        `/stock-items/categories/${encodeURIComponent(name)}`,
      );
      if (res.data.success) {
        const remaining = categories.filter((c) => c !== name);
        setCategories(remaining);
        if (kategori === name && remaining.length) setKategori(remaining[0]);
        popup.success("Data berhasil dihapus");
      } else {
        popup.alert(res.data.message || "Gagal menghapus kategori");
      }
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal menghapus kategori");
    }
  };

  /* ================= EXPORT EXCEL ================= */
  const exportToExcel = () => {
    if (items.length === 0) return popup.alert("Data masih kosong");

    const excelData = items.map((it, index) => ({
      No: index + 1,
      "Spesifikasi / Size": it.spesifikasi || "-",
      "Part Name": it.part_name,
      Lokasi: it.lokasi || "-",
      Maker: it.maker || "-",
      Stock: it.stock,
      "Minimum Stock": it.minimum_stock,
      Keterangan: it.keterangan || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 22 },
      { wch: 28 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 15 },
      { wch: 8 },
      { wch: 30 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Short Lifetime");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const fileData = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(fileData, `Stock_Short_Lifetime_${kategori.toUpperCase()}.xlsx`);
  };

  //Filter
  const filteredItems = items.filter((it) => {
    const keyword = search.toLowerCase();
    return (
      it.part_name?.toLowerCase().includes(keyword) ||
      it.spesifikasi?.toLowerCase().includes(keyword) ||
      it.lokasi?.toLowerCase().includes(keyword) ||
      it.maker?.toLowerCase().includes(keyword)
    );
  });

  /* ================= MODAL ================= */
  const openAddModal = () => {
    setIsEdit(false);
    setFormData(emptyItem);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setIsEdit(true);
    setFormData({ ...item });
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  /* ================= SAVE (ADD / EDIT) ================= */
  const saveItem = async () => {
    try {
      setLoading(true);

      if (isEdit) {
        // 1️⃣ UPDATE DATA ITEM
        await api.put(`/stock-items/${formData.id}`, {
          kode: formData.kode || "",
          part_name: formData.part_name,
          spesifikasi: formData.spesifikasi,
          lokasi: formData.lokasi,
          maker: formData.maker,
          minimum_stock: formData.minimum_stock,
          keterangan: formData.keterangan,
        });

        // 2️⃣ ADJUST STOCK (kalau stock berubah)
        await api.post(`/stock-items/${formData.id}/adjust`, {
          actual_stock: Number(formData.stock),
          note: formData.keterangan || "Opname Stock",
        });
      } else {
        // 🔥 TAMBAH ITEM BARU
        if (!formData.part_name) {
          popup.alert("Part Name wajib diisi");
          return;
        }

        if (Number(formData.stock) <= 0) {
          popup.alert("Stock Awal harus lebih besar dari 0");
          return;
        }

        await api.post("/stock-items", {
          lifetime_type: "short", // 🔥 WAJIB SHORT
          category: kategori,
          spesifikasi: formData.spesifikasi || "",
          part_name: formData.part_name,
          lokasi: formData.lokasi || "",
          maker: formData.maker || "",
          stock: Number(formData.stock),
          minimum_stock: Number(formData.minimum_stock || 0),
        });
      }

      popup.success(isEdit ? "Data berhasil diperbarui" : "Data berhasil disimpan");
      closeModal();
      fetchItems();
    } catch (err) {
      console.error(err);
      popup.alert("❌ Gagal menyimpan data");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE ================= */
  const deleteItem = async (id) => {
    const ok = await popup.confirm("Yakin ingin menghapus item ini?");
    if (!ok) return;
    try {
      await api.delete(`/stock-items/${id}`);
      fetchItems();
      popup.success("Data berhasil dihapus");
    } catch (err) {
      console.error("DELETE ERROR:", err);
      popup.alert("❌ Gagal menghapus data");
    }
  };

  /* ================= RENDER ================= */
  return (
    <div
      className="shortlife-container"
      style={{ marginTop: "-25px", height: "calc(108vh - 120px)" }}
    >
      {/* HEADER */}
      <div className="header-bar">
        <h2>DATA STOCK SHORT LIFETIME</h2>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            Export Excel
          </Button>
          <Button variant="primary" onClick={openAddModal}>
            Tambah Item
          </Button>
        </div>
      </div>

      {/* KATEGORI */}
      <div className="category-buttons">
        {categories.map((k) => (
          <span key={k} className="cat-chip">
            <button
              className={kategori === k ? "active" : ""}
              onClick={() => setKategori(k)}
            >
              {k.toUpperCase()}
            </button>
            <button
              type="button"
              className="cat-del"
              title={`Hapus kategori ${k.toUpperCase()}`}
              onClick={() => handleDeleteCategory(k)}
            >
              ×
            </button>
          </span>
        ))}

        <button
          type="button"
          className="cat-add"
          onClick={() => setShowCatModal(true)}
        >
          + Kategori
        </button>

        <Form.Control
          type="text"
          placeholder="🔍 Cari"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button variant="danger bg-danger" onClick={() => setSearch("")}>
          Reset
        </Button>
      </div>

      {/* MODAL TAMBAH KATEGORI (admin) */}
      <Modal
        show={showCatModal}
        onHide={() => setShowCatModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Tambah Kategori</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Nama Kategori</Form.Label>
            <Form.Control
              autoFocus
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCategory();
              }}
              placeholder="Contoh: outer"
            />
            <Form.Text className="text-muted">
              Kategori langsung muncul sebagai tombol filter.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCatModal(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleAddCategory}
            disabled={savingCat}
          >
            {savingCat ? "Menyimpan..." : "Tambah"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL */}
      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? "Edit Data Item (Opname Stock)" : "Tambah Item Stock"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {["Spesifikasi", "Part Name", "Lokasi", "Maker"].map((field) => (
              <Form.Group className="mb-2" key={field}>
                <Form.Label>{field}</Form.Label>
                <Form.Control
                  value={formData[field.toLowerCase().replace(" ", "_")] || ""}
                  onChange={(e) =>
                    handleChange(
                      field.toLowerCase().replace(" ", "_"),
                      e.target.value,
                    )
                  }
                />
              </Form.Group>
            ))}

            <div className="row">
              <div className="col">
                <Form.Label>
                  {isEdit ? "Stock Aktual (Opname)" : "Stock Awal"}
                </Form.Label>

                <Form.Control
                  type="number"
                  value={formData.stock || ""}
                  onChange={(e) => handleChange("stock", e.target.value)}
                />
              </div>

              {/* MINIMUM STOCK HANYA TAMBAH ITEM */}
              {!isEdit && (
                <div className="col">
                  <Form.Label>Minimum Stock</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.minimum_stock || ""}
                    onChange={(e) =>
                      handleChange("minimum_stock", e.target.value)
                    }
                  />
                </div>
              )}
            </div>

            <Form.Group className="mt-2">
              <Form.Label>Keterangan</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.keterangan || ""}
                onChange={(e) => handleChange("keterangan", e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Batal
          </Button>
          <Button variant="primary" onClick={saveItem} disabled={loading}>
            {loading ? "Menyimpan..." : "Simpan"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* TABLE */}
      <div className="table-wrapper mt-0">
        <table className="preview-table">
          <thead>
            <tr>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Part Number
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Part Name
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Lokasi</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Maker</th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Stock Minimun
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>
                Stock Actual
              </th>
              <th style={{ background: "#1e3a8a", color: "#fff" }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty">
                  Data belum ada
                </td>
              </tr>
            ) : (
              filteredItems.map((it, i) => (
                <tr
                  key={it.id}
                  className={it.stock <= it.minimum_stock ? "low-stock" : ""}
                >
                  <td className="center">{i + 1}</td>
                  <td className="cell-left">{it.spesifikasi || "-"}</td>
                  <td className="bold cell-left">{it.part_name}</td>
                  <td className="center">{it.lokasi || "-"}</td>
                  <td className="center">{it.maker || "-"}</td>
                  <td className="center">{it.minimum_stock}</td>
                  <td className="center">{it.stock}</td>

                  <td className="center">
                    <Button
                      size="sm"
                      variant="primary"
                      className="me-1 text-light "
                      onClick={() => {
                        openEditModal(it);
                      }}
                    >
                      Edit
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => deleteItem(it.id)}
                    >
                      Hapus
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
